import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import ErrorWithCountdown from '#/shared/components/ErrorWithCountdown';
import {
  useGetAlertsQuery,
  useGetRouteBasicsQuery,
} from '#/services/developerApi';
import ServiceAlertsList from '#/transit-tracker/components/ServiceAlertsList';
import SystemWideAlertList from '#/transit-tracker/components/SystemWideAlertList';
import DetailViewLayout from '#/components/DetailViewLayout';
import DetailNavHeader from '#/shared/components/DetailNavHeader';
import SEO from '#/shared/components/SEO';
import ROUTES from '#/constants/routes';
import useNavigate from '#/shared/hooks/useNavigate';
import useViewMapControls from '#/map/hooks/useViewMapControls';
import returnMostRecentAlert from '#/shared/utils/most-recent-alert';
import { ALERTS_POLLING_INTERVAL } from '#/transit-tracker/constants';
import { routeLookupToSortedList } from '#/transit-tracker/utils/routes';

const AlertsBodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
`;

function ServiceAlertsListContainer() {
  const { navigate, generateNavigateBack, generateNavigateClose } =
    useNavigate();

  const { serviceAlertsViewOpened, viewClosed } = useViewMapControls();

  useEffect(() => {
    serviceAlertsViewOpened();
    return viewClosed;
  }, [serviceAlertsViewOpened, viewClosed]);

  const navigateToRouteAlerts = id => navigate(ROUTES.alertsById, id);

  const {
    data: alertsData,
    isError: isAlertsError,
    isLoading: alertsAreLoading,
  } = useGetAlertsQuery(undefined, {
    pollingInterval: ALERTS_POLLING_INTERVAL,
  });

  const {
    data: routesLookup,
    isError: isRoutesError,
    isLoading: routesAreLoading,
  } = useGetRouteBasicsQuery();

  const routeList = useMemo(
    () => routeLookupToSortedList(routesLookup),
    [routesLookup]
  );

  if (alertsAreLoading || routesAreLoading) {
    return null;
  }

  const mostRecentAlert = returnMostRecentAlert(alertsData);

  return (
    <>
      <SEO metadataId="alerts" />
      <DetailViewLayout
        top={
          <DetailNavHeader
            onBack={generateNavigateBack()}
            onClose={generateNavigateClose()}
            content={<FormattedMessage id="alerts.servicealertstitle" />}
          />
        }
        bottom={
          ((isAlertsError || isRoutesError) && (
            <ErrorWithCountdown errorMessageId="system-error.warning" />
          )) || (
            <AlertsBodyContainer>
              {alertsData.hasSystemWideAlert && (
                <SystemWideAlertList systemWideAlerts={mostRecentAlert} />
              )}
              <ServiceAlertsList
                alertCountsByRoute={alertsData.alertCountsByRoute}
                routes={routeList}
                navigateToRouteAlerts={navigateToRouteAlerts}
              />
            </AlertsBodyContainer>
          )
        }
      />
    </>
  );
}

export default ServiceAlertsListContainer;
